import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Upload, ArrowLeft } from "lucide-react";

const empty = {
  name: "", price: "", description: "", era: "", metal: "Gold",
  year: "", origin: "", condition: "", image_url: "", images: [],
  featured: false, sold: false,
};

const AdminCoinForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    api.get(`/coins/${id}`)
      .then(({ data }) => {
        const images = (data.images && data.images.length) ? data.images : (data.image_url ? [data.image_url] : []);
        setForm({ ...empty, ...data, images, price: String(data.price) });
      })
      .catch(() => toast.error("Coin not found"))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      setForm((f) => ({ ...f, images: [...(f.images || []), ...uploaded] }));
      toast.success(uploaded.length > 1 ? `${uploaded.length} images uploaded` : "Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const makeCover = (idx) => {
    setForm((f) => {
      const imgs = [...f.images];
      const [chosen] = imgs.splice(idx, 1);
      return { ...f, images: [chosen, ...imgs] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price) || 0, image_url: form.images?.[0] || "" };
      if (isEdit) {
        await api.put(`/admin/coins/${id}`, payload);
        toast.success("Coin updated");
      } else {
        await api.post("/admin/coins", payload);
        toast.success("Coin added to catalog");
      }
      navigate("/admin/coins");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-24 font-display text-2xl text-[#A19D94]">Loading…</div>;

  return (
    <div className="max-w-4xl">
      <Link to="/admin/coins" className="coin-tag text-[#A19D94] hover:text-[#D4AF37] inline-flex items-center gap-2 mb-6">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back
      </Link>
      <div className="coin-tag mb-2">{isEdit ? "Edit Specimen" : "New Specimen"}</div>
      <h1 className="font-display text-4xl tracking-tight mb-10">{isEdit ? form.name || "Edit Coin" : "Add a Coin"}</h1>

      <form onSubmit={submit} className="space-y-8" data-testid="coin-form">
        <div className="border border-[#F3EFE0]/10 bg-[#0A0A0A] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="coin-tag">Photos</div>
            <div className="text-xs text-[#6B6A65]">{form.images?.length || 0} added · first image is the cover</div>
          </div>
          <div className="flex flex-wrap items-center gap-4" data-testid="coin-images-grid">
            {(form.images || []).map((url, idx) => (
              <div key={url + idx} className="relative group w-32 h-32 bg-black border border-[#F3EFE0]/10" data-testid={`coin-image-${idx}`}>
                <img src={fileUrl(url)} alt={`Coin ${idx + 1}`} className="w-full h-full object-contain" />
                {idx === 0 && (
                  <span className="absolute top-1 left-1 coin-tag bg-[#D4AF37]/90 text-black px-1.5 py-0.5 text-[10px]">Cover</span>
                )}
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                  {idx !== 0 && (
                    <button type="button" onClick={() => makeCover(idx)} className="text-[10px] text-[#D4AF37] border border-[#D4AF37]/50 px-2 py-1 hover:bg-[#D4AF37] hover:text-black transition-colors" data-testid={`make-cover-${idx}`}>
                      Set as cover
                    </button>
                  )}
                  <button type="button" onClick={() => removeImage(idx)} className="text-[10px] text-[#C5A059] border border-[#C5A059]/50 px-2 py-1 hover:bg-[#5C2B29] hover:text-[#F3EFE0] transition-colors" data-testid={`remove-image-${idx}`}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <label className="w-32 h-32 border border-dashed border-[#F3EFE0]/20 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all cursor-pointer flex flex-col items-center justify-center gap-2 text-xs text-[#A19D94]" data-testid="upload-image-label">
              <Upload className="w-5 h-5" strokeWidth={1.5} />
              {uploading ? "Uploading…" : "Add Images"}
              <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" disabled={uploading} data-testid="upload-image-input" />
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Name *">
            <input required value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} data-testid="coin-name-input" />
          </Field>
          <Field label="Price (USD) *">
            <input required type="number" min="0" step="0.01" value={form.price} onChange={(e) => update("price", e.target.value)} className={inputCls} data-testid="coin-price-input" />
          </Field>
          <Field label="Category">
            <input value={form.era} onChange={(e) => update("era", e.target.value)} placeholder="e.g. Ancient, Modern, Commemorative" className={inputCls} data-testid="coin-era-input" />
          </Field>
          <Field label="Metal">
            <select value={form.metal} onChange={(e) => update("metal", e.target.value)} className={inputCls + " appearance-none"} data-testid="coin-metal-input">
              {["Gold", "Silver", "Bronze", "Copper", "Platinum", "Nickel", "Mixed", "Other"].map((m) => (
                <option key={m} value={m} className="bg-[#0A0A0A]">{m}</option>
              ))}
            </select>
          </Field>
          <Field label="Year">
            <input value={form.year} onChange={(e) => update("year", e.target.value)} placeholder="e.g. 1789 or 27 BCE" className={inputCls} data-testid="coin-year-input" />
          </Field>
          <Field label="Origin">
            <input value={form.origin} onChange={(e) => update("origin", e.target.value)} placeholder="e.g. Rome, Italy" className={inputCls} data-testid="coin-origin-input" />
          </Field>
          <Field label="Condition">
            <input value={form.condition} onChange={(e) => update("condition", e.target.value)} placeholder="e.g. Fine, VF, Uncirculated" className={inputCls} data-testid="coin-condition-input" />
          </Field>
        </div>

        <Field label="Description *">
          <textarea required rows={6} value={form.description} onChange={(e) => update("description", e.target.value)} className="w-full bg-transparent border border-[#F3EFE0]/15 focus:border-[#D4AF37] outline-none p-3 text-[#F3EFE0] resize-none" data-testid="coin-description-input" />
        </Field>

        <div className="flex gap-6 flex-wrap">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => update("featured", e.target.checked)} className="w-4 h-4 accent-[#D4AF37]" data-testid="coin-featured-input" />
            <span className="coin-tag">Featured</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.sold} onChange={(e) => update("sold", e.target.checked)} className="w-4 h-4 accent-[#D4AF37]" data-testid="coin-sold-input" />
            <span className="coin-tag">Sold</span>
          </label>
        </div>

        <div className="flex gap-4 border-t border-[#F3EFE0]/10 pt-6">
          <button type="submit" disabled={saving} className="bg-[#D4AF37] text-black font-medium px-8 py-4 hover:bg-[#F1C40F] transition-colors disabled:opacity-50" data-testid="coin-save-btn">
            {saving ? "Saving…" : isEdit ? "Update Coin" : "Add to Catalog"}
          </button>
          <Link to="/admin/coins" className="border border-[#F3EFE0]/20 px-8 py-4 hover:border-[#D4AF37]/60 transition-all">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

const inputCls = "w-full bg-transparent border-b border-[#F3EFE0]/20 focus:border-[#D4AF37] outline-none py-2 text-[#F3EFE0]";

const Field = ({ label, children }) => (
  <div>
    <label className="coin-tag block mb-2">{label}</label>
    {children}
  </div>
);

export default AdminCoinForm;
